import { memo, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion as Motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import cls from "./Home.module.scss";

import LogoS02 from "@/assets/NEW/logo/S02.webp";

import MrM from "@/assets/UI/home/MrM.webp";
import MrsM from "@/assets/UI/home/MrsM.webp";
import MrD from "@/assets/UI/home/MrD.webp";
import MrsD from "@/assets/UI/home/MrsD.webp";

import whoIcon from "@/assets/UI/who.webp";
import netWorkIcon from "@/assets/UI/notyNetwork.webp";
import exploreIcon from "@/assets/UI/Explore.webp";
import loginIcon from "@/assets/UI/login.webp";
import citizenIcon from "@/assets/UI/citizen.webp";

const STATUS_ITEMS = [
  {
    key: "live.citizensOnline",
    fallback: "Citizens online across the universe",
  },
  { key: "live.liveDiscussions", fallback: "Live discussions forming now" },
  { key: "live.connectionsForming", fallback: "New connections forming" },
  { key: "live.universeExpanding", fallback: "The universe is expanding" },
];

const ACTION_LINKS = [
  {
    to: "/universe",
    labelKey: "actions.exploreUniverse",
    fallback: "Explore Universe",
    icon: exploreIcon,
    variant: "vBlue",
  },
  {
    to: "/noty-network",
    labelKey: "actions.notyVlog",
    fallback: "Noty Network",
    icon: netWorkIcon,
    variant: "vPurple",
  },
  {
    to: "/terms",
    labelKey: "actions.aboutRules",
    fallback: "Rules & Terms",
    icon: whoIcon,
    variant: "vPink",
  },
  {
    to: "/login",
    labelKey: "actions.logIn",
    fallback: "Log In",
    icon: loginIcon,
    variant: "vGold",
  },
  {
    to: "/register",
    labelKey: "actions.register",
    fallback: "Become a Citizen",
    icon: citizenIcon,
    variant: "vMint",
    isPrimary: true,
  },
];

const HERO_LAYERS = {
  mobile: [
    { src: MrM, className: cls.z0, width: 1080, height: 1920, variants: "a" },
    { src: MrsM, className: cls.z1, width: 1080, height: 1920, variants: "b" },
  ],
  desktop: [
    { src: MrD, className: cls.z0, width: 1920, height: 1080, variants: "a" },
    { src: MrsD, className: cls.z1, width: 1920, height: 1080, variants: "b" },
  ],
};

const DISTRICTS = [
  ["N", "ova"],
  ["O", "racle"],
  ["T", "erra"],
  ["Y", "ara"],
];

const FLOAT_VARIANTS = {
  a: {
    hidden: { opacity: 0.55 },
    show: {
      opacity: [0.5, 0.68, 0.5],
      scale: [1.02, 1.05, 1.02],
      x: [0, 6, 0],
      transition: { duration: 13, ease: "easeInOut", repeat: Infinity },
    },
  },
  b: {
    hidden: { opacity: 0.78 },
    show: {
      opacity: [0.78, 0.62, 0.78],
      scale: [1.05, 1.02, 1.05],
      x: [0, -8, 0],
      transition: { duration: 15, ease: "easeInOut", repeat: Infinity },
    },
  },
};

const LOGO_MOTION = {
  initial: { opacity: 0, y: -12, scale: 0.88 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.7, ease: "easeOut" },
};

const CARD_MOTION = {
  initial: { opacity: 0, y: 18, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.55, ease: "easeOut", delay: 0.12 },
};

function textOrFallback(value, fallback) {
  return typeof value === "string" && value.trim() ? value : fallback;
}

function getMotionProps(reduceMotion, props) {
  return reduceMotion
    ? { initial: false, animate: false, transition: undefined }
    : props;
}

function getMediaQueryMatch(query) {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(query).matches
  );
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => getMediaQueryMatch(query));

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event) => setMatches(event.matches);

    setMatches(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange);
      return () => mediaQueryList.removeEventListener("change", handleChange);
    }

    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [query]);

  return matches;
}

const BackgroundFx = memo(function BackgroundFx() {
  return (
    <div className={cls.universeFx} aria-hidden="true">
      <span className={cls.orbitOne} />
      <span className={cls.orbitTwo} />
      <span className={cls.energyLine} />
      <span className={cls.particles} />
    </div>
  );
});

const HeroImageLayer = memo(function HeroImageLayer({
  className,
  layers,
  reduceMotion,
}) {
  return (
    <div className={className} aria-hidden="true">
      {layers.map(({ src, className: zClass, width, height, variants }) => (
        <Motion.img
          key={src}
          className={`${cls.heroImg} ${zClass}`}
          src={src}
          alt=""
          width={width}
          height={height}
          variants={reduceMotion ? undefined : FLOAT_VARIANTS[variants]}
          initial={reduceMotion ? false : "hidden"}
          animate={reduceMotion ? false : "show"}
          decoding="async"
          loading="eager"
          fetchPriority="high"
          draggable="false"
        />
      ))}
    </div>
  );
});

const BrandMark = memo(function BrandMark({
  reduceMotion,
  brandLabel,
  districtsLabel,
}) {
  return (
    <Motion.div
      className={cls.logoStack}
      {...getMotionProps(reduceMotion, LOGO_MOTION)}
    >
      <div className={cls.logoPortal}>
        <span className={cls.logoGlow} />
        <span className={cls.logoRing} />
        <img
          src={LogoS02}
          alt={brandLabel}
          className={cls.logo}
          width="500"
          height="500"
          decoding="async"
          fetchPriority="high"
        />
      </div>

      <div className={cls.districtMorph} aria-label={districtsLabel}>
        <div className={cls.districtNames}>
          {DISTRICTS.map(([letter, rest]) => (
            <span key={letter}>
              <strong>{letter}</strong>
              <em>{rest}</em>
            </span>
          ))}
        </div>

        <div className={cls.notyWord}>
          <span>NOTY</span>
          <small>UNIVERSE</small>
        </div>
      </div>
    </Motion.div>
  );
});

const LiveStrip = memo(function LiveStrip({ items }) {
  return (
    <div className={cls.liveStrip}>
      <span className={cls.liveDot} aria-hidden="true" />

      <div className={cls.liveText} aria-live="polite">
        {items.map((item, index) => (
          <span key={`${item}-${index}`}>{item}</span>
        ))}
      </div>
    </div>
  );
});

const ActionNav = memo(function ActionNav({ links, navLabel }) {
  return (
    <nav className={cls.actionGrid} aria-label={navLabel}>
      {links.map(({ to, label, icon, variant, isPrimary }) => (
        <Link
          key={to}
          to={to}
          className={[
            cls.actionBtn,
            cls[variant],
            isPrimary ? cls.actionBtnWide : "",
            isPrimary ? cls.mainCta : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <span className={cls.actionIcon} aria-hidden="true">
            <img
              src={icon}
              alt=""
              width="500"
              height="500"
              loading="lazy"
              decoding="async"
            />
          </span>
          <span>{label}</span>
        </Link>
      ))}
    </nav>
  );
});

export default function Home() {
  const { t } = useTranslation("home");
  const reduceMotion = useReducedMotion();
  const isDesktop = useMediaQuery("(min-width: 980px)");
  const activeHeroLayers = isDesktop ? HERO_LAYERS.desktop : HERO_LAYERS.mobile;
  const heroLayerClassName = isDesktop ? cls.desktopOnly : cls.mobileOnly;

  const statusItems = useMemo(
    () =>
      STATUS_ITEMS.map(({ key, fallback }) =>
        textOrFallback(t(key, { defaultValue: fallback }), fallback),
      ).filter(Boolean),
    [t],
  );

  const actionLinks = useMemo(
    () =>
      ACTION_LINKS.map((action) => ({
        ...action,
        label: textOrFallback(
          t(action.labelKey, { defaultValue: action.fallback }),
          action.fallback,
        ),
      })),
    [t],
  );

  const homeText = (key, fallback) =>
    textOrFallback(t(key, { defaultValue: fallback }), fallback);

  const brandLabel = homeText("brand", "Noty Universe");
  const districtsLabel = homeText("districtsLabel", "Noty Universe districts");

  return (
    <main className={cls.page}>
      <section
        className={cls.heroStage}
        aria-label={homeText("ariaLabel", "Noty Universe portal")}
      >
        <BackgroundFx />

        <HeroImageLayer
          className={heroLayerClassName}
          layers={activeHeroLayers}
          reduceMotion={reduceMotion}
        />

        <div className={cls.darkVeil} aria-hidden="true" />

        <div className={cls.overlay}>
          <BrandMark
            reduceMotion={reduceMotion}
            brandLabel={brandLabel}
            districtsLabel={districtsLabel}
          />

          <Motion.div
            className={cls.portalCard}
            {...getMotionProps(reduceMotion, CARD_MOTION)}
          >
            <div className={cls.introBox}>
              <span className={cls.introKicker}>
                {homeText("intro.kicker", "Noty Universe")}
              </span>

              <h1>
                {homeText("intro.titleBefore", "Welcome to the")}{" "}
                <span>
                  {homeText("intro.titleHighlight", "next generation")}
                </span>{" "}
                {homeText("intro.titleAfter", "of human connection.")}
              </h1>

              <p className={cls.introSub}>
                {homeText("intro.subtitleBefore", "Where")}{" "}
                <strong>
                  {homeText("intro.subtitleHighlight", "free thinkers")}
                </strong>
                ,{" "}
                {homeText(
                  "intro.subtitleAfter",
                  "creators, builders, and open minds connect before the rest of the world catches up.",
                )}
              </p>
            </div>

            <LiveStrip items={statusItems} />
            <ActionNav
              links={actionLinks}
              navLabel={homeText("navLabel", "Home actions")}
            />
          </Motion.div>
        </div>
      </section>
    </main>
  );
}
